/*
 * Thin progressive-enhancement layer. The server (Go + templ) owns all state and
 * rendering; htmx (vendored, loaded before this file) does the network swaps. This
 * file only adds the few behaviours native elements + htmx do not give us for free:
 *
 *   1. opening native <dialog> modals from declarative attributes,
 *   2. auto-dismissing server-pushed toasts, and
 *   3. toggling the color theme (persisted in a cookie so the server renders the
 *      correct theme on the next request with no flash).
 *
 * Keep it small. If something can be done with a native element or an htmx attribute,
 * do it there instead of here. No framework, no Alpine.
 */

/**
 * Initialise the browser Sentry SDK (vendored, loaded via <script> when the server
 * has a frontend DSN configured). No-op when the script or meta tags are absent.
 */
function initSentry(): void {
  if (typeof Sentry === 'undefined') return;
  const dsn = document.querySelector<HTMLMetaElement>('meta[name="sentry-dsn"]')?.content;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: document.querySelector<HTMLMetaElement>('meta[name="sentry-environment"]')
      ?.content,
    release: document.querySelector<HTMLMetaElement>('meta[name="sentry-release"]')?.content,
  });
}

initSentry();

const TOAST_TTL_MS = 4000;
const TOAST_LEAVE_MS = 200;

/**
 * Close any open <dialog> when the server responds with an HX-Trigger:
 * close-dialog header — the modal-CRUD pattern's success path.
 */
function setupDialogClose(): void {
  document.body.addEventListener('close-dialog', () => {
    for (const dialog of document.querySelectorAll('dialog[open]')) {
      if (dialog instanceof HTMLDialogElement) dialog.close();
    }
  });
}

/** Open/close native <dialog> elements via [data-dialog-open="id"] / [data-dialog-close]. */
function setupDialogs(): void {
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const opener = target.closest<HTMLElement>('[data-dialog-open]');
    if (opener) {
      const id = opener.dataset.dialogOpen;
      const dialog = id ? document.getElementById(id) : null;
      if (dialog instanceof HTMLDialogElement) {
        dialog.showModal();
      }
      return;
    }

    const closer = target.closest<HTMLElement>('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog instanceof HTMLDialogElement) {
        dialog.close();
      }
    }
  });
}

/** Remove a toast after a delay, playing the leave transition first. */
function scheduleDismiss(toast: HTMLElement): void {
  window.setTimeout(() => {
    toast.classList.add('toast--leaving');
    window.setTimeout(() => toast.remove(), TOAST_LEAVE_MS);
  }, TOAST_TTL_MS);
}

/** Watch the toast region for server-pushed (htmx OOB) toasts and auto-dismiss them. */
function setupToasts(): void {
  const container = document.getElementById('toasts');
  if (!container) {
    return;
  }

  for (const existing of container.querySelectorAll<HTMLElement>('.toast')) {
    scheduleDismiss(existing);
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement && node.classList.contains('toast')) {
          scheduleDismiss(node);
        }
      }
    }
  });
  observer.observe(container, { childList: true });
}

/**
 * Toggle dark/light from [data-theme-toggle]. AnchoraHub is dark-first: no
 * attribute means the brand dark theme, so the toggle flips between the two
 * explicit values. The choice is written to the `theme` cookie so the server
 * renders <html data-theme="..."> on the next request (no flash-of-wrong-theme).
 */
function setupThemeToggle(): void {
  const LABELS: Record<string, string> = { light: 'Luminos', dark: 'Întunecat' };

  function applyTheme(theme: string): void {
    document.documentElement.dataset.theme = theme;
    // biome-ignore lint/suspicious/noDocumentCookie: a plain cookie is intentional — the Go server reads it to render <html data-theme> server-side; the Cookie Store API is async.
    document.cookie = `theme=${theme}; path=/; max-age=31536000; samesite=lax`;
    const label = document.querySelector('[data-theme-label]');
    if (label) label.textContent = LABELS[theme] ?? theme;
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-theme-toggle]')) {
      return;
    }
    const current = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
    applyTheme(current === 'dark' ? 'light' : 'dark');
  });
}

/**
 * Convert a base64url-encoded VAPID public key into the Uint8Array the
 * PushManager.subscribe applicationServerKey option expects.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/**
 * POST a subscription payload to a push endpoint. Same-origin fetch: the browser
 * sets Sec-Fetch-Site itself, which is what the server's CrossOriginProtection
 * checks — there is no token to attach.
 */
async function postPush(url: string, body: unknown): Promise<boolean> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.ok;
}

/**
 * Wire the "Notificări browser" toggle on the prefs page. Subscribing asks for
 * permission, registers the root-scope service worker, creates a PushManager
 * subscription, and posts it to the server. Unsubscribing reverses it. All
 * behaviour is behind feature detection; the toggle is left untouched when the
 * browser lacks Push support.
 */
function setupPush(): void {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return;
  }
  const toggle = document.querySelector<HTMLInputElement>('[data-push-toggle]');
  if (!toggle) {
    return;
  }
  const el = toggle;
  const vapidKey = el.dataset.vapidKey;
  if (!vapidKey) {
    return;
  }
  const label = document.querySelector<HTMLElement>('[data-push-label]');

  // Reflect the current subscription state in the toggle on load.
  void navigator.serviceWorker.getRegistration('/sw.js').then(async (reg) => {
    if (!reg) return;
    const existing = await reg.pushManager.getSubscription();
    el.checked = existing !== null;
  });

  const enable = async (): Promise<void> => {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      el.checked = false;
      if (label) label.textContent = 'Permisiune refuzată';
      return;
    }
    const reg = await navigator.serviceWorker.register('/sw.js');
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
    const ok = await postPush('/notificari/push/abonare', sub.toJSON());
    el.checked = ok;
  };

  const disable = async (): Promise<void> => {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      await postPush('/notificari/push/dezabonare', { endpoint: sub.endpoint });
      await sub.unsubscribe();
    }
    el.checked = false;
  };

  el.addEventListener('change', () => {
    el.disabled = true;
    const action = el.checked ? enable() : disable();
    action
      .catch(() => {
        el.checked = false;
      })
      .finally(() => {
        el.disabled = false;
      });
  });
}

/**
 * Fill the hidden context fields on the support form so every ticket carries the
 * page it was opened from and the asset build that was live — data a free-text
 * description almost never includes. The browser/user-agent is captured
 * server-side; this only adds what the server cannot see. No-op when the form
 * (or its hidden inputs) is absent.
 */
function setupTicketContext(): void {
  const form = document.querySelector<HTMLFormElement>('[data-ticket-form]');
  if (!form) {
    return;
  }
  const page = form.querySelector<HTMLInputElement>('input[name="pagina"]');
  if (page && !page.value && document.referrer) {
    try {
      const ref = new URL(document.referrer);
      if (ref.origin === window.location.origin) {
        page.value = ref.pathname + ref.search;
      }
    } catch {
      // Malformed referrer — leave the field blank.
    }
  }
  const version = form.querySelector<HTMLInputElement>('input[name="versiune"]');
  if (version && !version.value) {
    const src = document.querySelector<HTMLScriptElement>('script[src*="/static/app."]')?.src ?? '';
    version.value = src.match(/app\.([a-f0-9]+)\.js/)?.[1] ?? '';
  }
}

function init(): void {
  setupDialogs();
  setupDialogClose();
  setupToasts();
  setupThemeToggle();
  setupPush();
  setupTicketContext();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
